import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  Center,
  Divider,
  Heading,
  HStack,
  Image,
  Link,
  ListItem,
  Stack,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tr,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback } from "react";
import ReactAudioPlayer from "react-audio-player";

interface IPerson {
  name: string | JSX.Element;
  socialHandleLink?: string;
}

interface ISpouse {
  name: string;
  ex?: boolean;
  socialHandleLink?: string;
}
interface IMedia {
  displayName: string;
  link?: string;
}

interface IFraudCardGermanProps {
  fraudsterImage?: string;
  fraudsterName: string;
  fraudsterDescription: string | JSX.Element;
  partners?: Array<IPerson>;
  spouses?: Array<ISpouse>;
  techniques?: string[];
  estimatedMoneyLost: string;
  voiceSamples?: string[];
  educationalMedia?: Array<IMedia>;
  images?: Array<string>;
  documents?: string[];
  socialMediaLinks: React.ReactNode;
}

export default function FraudCardGerman({
  fraudsterImage,
  fraudsterName,
  fraudsterDescription,
  estimatedMoneyLost,
  techniques,
  spouses,
  partners,
  educationalMedia,
  images,
  voiceSamples,
  documents,
  socialMediaLinks,
}: IFraudCardGermanProps) {
  const openPDF = useCallback(
    (document: string) => () => {
      const fileUrl = document;
      window.open(fileUrl, "_blank");
    },
    []
  );

  return (
    <Box>
      <Center>
        <Card maxW="2xl">
          <CardBody>
            <Center>
              <Image
                src={fraudsterImage}
                alt="Betrüger Foto"
                borderRadius="lg"
                width={"sm"}
              />
            </Center>
            <Stack mt="6" spacing="3">
              <Center>
                <Heading size="md">{fraudsterName}</Heading>
              </Center>
              {fraudsterDescription}
              <Text fontWeight={600}>
                {`Techniken, die ${fraudsterName} verwendet, um Opfer zu betrügen`}
              </Text>
              <UnorderedList spacing={"2"}>
                {techniques?.map((technique, index) => (
                  <ListItem key={index}>{technique}</ListItem>
                ))}
              </UnorderedList>
            </Stack>
          </CardBody>
          <Divider />
          <CardBody>
            <TableContainer>
              <Table variant="simple">
                <TableCaption>
                  Handeln Sie mit äußerster Vorsicht. Sie wurden gewarnt.
                </TableCaption>

                <Tbody>
                  <Tr>
                    <Td>Partner</Td>
                    <Td>
                      {partners?.map((partner, index) =>
                        partner.socialHandleLink ? (
                          <Link
                            key={index}
                            href={partner.socialHandleLink}
                            isExternal
                            color={"blue.600"}
                          >
                            {partner.name}
                          </Link>
                        ) : (
                          <Text key={index}>{partner.name}</Text>
                        )
                      )}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>Geschätztes gestohlenes Geld</Td>
                    <Td>{estimatedMoneyLost}</Td>
                  </Tr>
                  <Tr>
                    <Td>Social Media Links</Td>
                    <Td>
                      <Text>{socialMediaLinks}</Text>
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>Anzahl der Opfer</Td>
                    <Td>8 oder mehr</Td>
                  </Tr>
                  {spouses && (
                    <Tr>
                      <Td>Familienstand</Td>
                      <Td>Geschieden</Td>
                    </Tr>
                  )}
                  {spouses && (
                    <Tr>
                      <Td>Ehepartner</Td>
                      <Td>
                        {spouses?.map((spouse, index) => (
                          <React.Fragment key={index}>
                            <Link
                              href={spouse.socialHandleLink}
                              isExternal
                              color={"blue.500"}
                            >
                              {spouse.name}
                            </Link>
                            <span> {spouse.ex ? "(ex)" : ""} </span>
                          </React.Fragment>
                        ))}
                      </Td>
                    </Tr>
                  )}

                  {/* Documents */}
                  {voiceSamples && (
                    <Tr>
                      <Td>Stimmproben</Td>
                      <Td>
                        <VStack
                          alignSelf={"flex-start"}
                          spacing={"2"}
                          left={-10}
                          position={"relative"}
                        >
                          {voiceSamples?.map((voiceSample, index) => (
                            <ReactAudioPlayer
                              key={index}
                              src={voiceSample}
                              controls
                            />
                          ))}
                        </VStack>
                      </Td>
                    </Tr>
                  )}
                  {documents && (
                    <Tr>
                      <Td>Verwendete Dokumente</Td>
                      <Td>
                        <HStack>
                          {documents?.map((document, index) => (
                            <Image
                              key={index}
                              maxW={10}
                              src={require("../assets/pdficon.avif")}
                              onClick={openPDF(document)}
                              cursor={"pointer"}
                            />
                          ))}
                        </HStack>
                      </Td>
                    </Tr>
                  )}
                  <Tr>
                    <Td>Ermittlung</Td>
                    <Td>In Bearbeitung</Td>
                  </Tr>
                  <Tr>
                    <Td>Verwandte Medien</Td>
                    <Td>
                      <VStack alignItems={"self-start"}>
                        {images?.map((image, index) => (
                          <Image key={index} maxW={200} src={image} />
                        ))}
                      </VStack>
                    </Td>
                  </Tr>
                  {educationalMedia && (
                    <Tr>
                      <Td>Bildungsinhalte</Td>
                      <Td>
                        <VStack
                          position={"relative"}
                          color="blue.600"
                          alignItems={"flex-start"}
                        >
                          {educationalMedia?.map((media, index) => (
                            <Link key={index} href={media.link} isExternal>
                              {media.displayName}
                            </Link>
                          ))}
                        </VStack>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
          <CardFooter>
            <Center width={"100%"}>
              <Link href="mailto:haysmhakim@gmail.com" isExternal mt={-6}>
                Können Sie helfen?
                <Button variant="solid" colorScheme="blue" ml={2}>
                  Kontakt aufnehmen
                </Button>
              </Link>
            </Center>
          </CardFooter>
        </Card>
      </Center>
    </Box>
  );
}
